/**
 * What the signed-in user may do to another member (user-management §4.5),
 * computed with the protocol's permission functions. The UI shows only what
 * this allows; the server checks every action again (rule 5).
 *
 * - The hierarchy applies to nicknames, timeouts, kicks and bans (and the
 *   roles you give, `assignableRoles`): you act only on people whose
 *   highest role is below yours. The owner outranks everyone and is never
 *   their target. Administrator does not lift the hierarchy (Discord's rule).
 * - Timeouts can't target the owner or Administrators.
 * - Voice actions (server mute, deafen, move, disconnect) have no hierarchy,
 *   as in Discord ("otherwise, permissions do not obey the role
 *   hierarchy"): anyone in voice, higher roles and the owner too (QA
 *   2026-10-28 follow-up round 2). They need the target to be in voice,
 *   and use the permissions of the channel they are in.
 * - Yourself too (QA 2026-10-28 follow-up round 3): Discord offers Server
 *   Mute and Server Deafen (and their undo), Move To and Disconnect on
 *   yourself when you hold the permission in your channel, and the server
 *   allows the same (`MembersService.modify` has no self rule). Before, a
 *   Voice Mod who server-muted the owner left the owner no undo in any of
 *   their own menus, although the PATCH with the owner's token succeeds.
 *   Move To and Disconnect on yourself (another device or tab of yours)
 *   need no permission since 2026-10-29: a switch or a leave, with the
 *   join rules (the server's `checkSelfMove`), as Discord's.
 * - A server mute or deafen outlasts a leave (it is stored on the member):
 *   while the target is out of voice only its undo is offered, with the
 *   server-level Mute / Deafen Members, as the server checks it
 *   (`MembersService.requireForVoiceTarget`). QA 2026-10-28 round 2: the
 *   menus read the flags from the voice state alone, so nobody could undo
 *   a mute once the person had left.
 * - A channel mute or deafen (2026-10-28) is for the voice channel the
 *   target is in now: Mute / Deafen Members there, with its overwrites (the
 *   server's rule, `VoiceMutesService`), and no hierarchy, as the server
 *   mute: so a member allow overwrite makes a channel-only moderator, even
 *   one with no role (review 2026-10-28). Out of that channel it is lifted
 *   from the channel's settings ("Muted in this channel"), never from a
 *   menu that cannot say which channel.
 * - Poke is an in-server feature (2026-10-29, the server's `PokesService`):
 *   nx Poke ("Poke members"; the owner holds it), and the target's roles'
 *   "Who can poke" (`nx_pokes_from`, `pokeAllowedByRoles`; the owner
 *   bypasses it) decide whether it is offered at all; you online and in a
 *   voice channel here, and the target online, whether it can be sent now
 *   (`pokeBlocked` says why not).
 */
import {
  ChannelType,
  NxPermissions,
  Permissions,
  isTimedOut,
  pokeAllowedByRoles,
  type Role,
  type Snowflake,
} from '@neosocial/protocol';
import type { DataState } from '../store/reducer';
import {
  basePermissions,
  can,
  canManageRole,
  canNx,
  channelPermissions,
  channelsInSidebarOrder,
  presenceOf,
  sortedRoles,
  topRolePosition,
  viewAsRoles,
} from '../store/selectors';

/**
 * The roles you can give to or take from a member, top first: Discord's
 * rule, as the server checks it (`MembersService.checkRoleAssignable`). You
 * hold Manage Roles, and the role is below your highest role (the owner:
 * every role); never @everyone or a managed role. The member's own rank does
 * not count (Discord lets you give a role below yours to anyone). Empty when
 * you can manage none.
 */
export function assignableRoles(d: DataState, guildId: Snowflake): Role[] {
  const g = d.guilds[guildId];
  if (!g || !d.me || !can(basePermissions(d, guildId), Permissions.MANAGE_ROLES)) return [];
  return sortedRoles(g).filter(
    (r) => r.id !== guildId && !r.managed && canManageRole(d, guildId, r),
  );
}

/**
 * Why a poke the rules allow cannot be sent right now, in the server's
 * order: `needs_online` (you are invisible, `POKE_NEEDS_ONLINE`),
 * `needs_voice` (you are in no voice channel here, `POKE_NEEDS_VOICE`),
 * `target_offline` (they are offline or invisible, `POKE_TARGET_OFFLINE`).
 */
export type PokeBlock = 'needs_online' | 'needs_voice' | 'target_offline';

/** The error string for each `PokeBlock` (the server's answer for it). */
export const POKE_BLOCK_KEYS: Record<PokeBlock, string> = {
  needs_online: 'ns.err.100045',
  needs_voice: 'ns.err.100046',
  target_offline: 'ns.err.100047',
};

/**
 * Poke for one target: whether it is offered (`shown`: you hold nx Poke and
 * their roles' `nx_pokes_from` lets you) and, when it is, why it cannot be
 * sent now (`blocked`). Blocks, their "Pokes from" and the rate limits are
 * the server's alone (they answer `POKE_FAILED` or 429).
 */
export function pokeState(
  d: DataState,
  guildId: Snowflake,
  targetId: Snowflake,
): { shown: boolean; blocked: PokeBlock | null } {
  const meId = d.me?.id ?? '';
  const g = d.guilds[guildId];
  const member = d.members[guildId]?.[targetId];
  const shown =
    !!g &&
    !!member &&
    targetId !== meId &&
    canNx(basePermissions(d, guildId), NxPermissions.POKE) &&
    // the owner's rank is infinite (none while viewing the server as a role)
    pokeAllowedByRoles(g.roles, guildId, topRolePosition(d, guildId, meId), member.roles);
  if (!shown) return { shown, blocked: null };
  const blocked: PokeBlock | null =
    presenceOf(d, meId) === 'offline'
      ? 'needs_online'
      : !d.voiceStates[guildId]?.[meId]?.channel_id
        ? 'needs_voice'
        : presenceOf(d, targetId) === 'offline'
          ? 'target_offline'
          : null;
  return { shown, blocked };
}

export interface MemberActions {
  isMe: boolean;
  /** The target is the server's owner. */
  targetIsOwner: boolean;
  /** You are the server's owner. */
  iAmOwner: boolean;
  /** You rank above the target (owner, or a higher top role). */
  outranks: boolean;
  /**
   * You hold a permission the hierarchy limits (nicknames, timeout, kick,
   * ban), but the target ranks at or above you.
   */
  blockedByHierarchy: boolean;
  voiceChannelId: Snowflake | null;
  timedOutUntil: string | null;
  serverMuted: boolean;
  serverDeafened: boolean;
  /** A moderator's channel mute / deafen in the channel they are in now. */
  channelMuted: boolean;
  channelDeafened: boolean;
  nick: boolean;
  /** A poke can be sent now (`pokeState`: offered, and nothing blocks it). */
  poke: boolean;
  /**
   * Poke is offered but cannot be sent now, and why (`PokeBlock`; the
   * string is `POKE_BLOCK_KEYS`): a menu may show the item disabled with
   * it. Null when it can be sent, or is not offered at all (hidden).
   */
  pokeBlocked: PokeBlock | null;
  timeout: boolean;
  kick: boolean;
  ban: boolean;
  mute: boolean;
  deafen: boolean;
  /** Channel mute / deafen (or their undo) in `voiceChannelId`. */
  channelMute: boolean;
  channelDeafen: boolean;
  move: boolean;
  disconnect: boolean;
  transfer: boolean;
  /** You can give or take at least one role (`assignableRoles`): the Roles submenu. */
  roles: boolean;
}

/** The member actions Discord's hierarchy limits (server-level permissions). */
const HIERARCHY_BITS =
  Permissions.KICK_MEMBERS |
  Permissions.BAN_MEMBERS |
  Permissions.MODERATE_MEMBERS |
  Permissions.MANAGE_NICKNAMES;

export function memberActions(
  d: DataState,
  guildId: Snowflake,
  targetId: Snowflake,
  now: number = Date.now(),
): MemberActions {
  const meId = d.me?.id ?? '';
  const g = d.guilds[guildId];
  const isMe = targetId === meId;
  // While viewing the server as a role you are not the owner ("View server as role").
  const iAmOwner = !!g && g.owner_id === meId && !viewAsRoles(d, guildId);
  const targetIsOwner = !!g && g.owner_id === targetId;
  const base = basePermissions(d, guildId);
  const outranks =
    !isMe &&
    !targetIsOwner &&
    (iAmOwner || topRolePosition(d, guildId, meId) > topRolePosition(d, guildId, targetId));
  const member = d.members[guildId]?.[targetId];
  const until = member?.communication_disabled_until ?? null;
  const timedOutUntil = until && isTimedOut(until, now) ? until : null;
  const vs = d.voiceStates[guildId]?.[targetId];
  const voiceChannelId = vs?.channel_id ?? null;
  const vc = voiceChannelId ? d.channels[voiceChannelId] : undefined;
  const vperm = vc ? channelPermissions(d, vc) : base;
  const targetAdmin = can(basePermissions(d, guildId, targetId), Permissions.ADMINISTRATOR);
  const inVoice = !!voiceChannelId;
  // the voice state while in voice (the newest word there), else the member
  // record (GUILD_MEMBER_UPDATE keeps it current after a leave)
  const serverMuted = vs ? !!vs.mute : !!member?.mute;
  const serverDeafened = vs ? !!vs.deaf : !!member?.deaf;
  const holdsRanked = (base.permissions & HIERARCHY_BITS) !== 0n;
  const mod = (ok: boolean) => !isMe && outranks && ok;
  const channelMuted = !!vs?.nx_channel_mute;
  const channelDeafened = !!vs?.nx_channel_deaf;
  const poke = pokeState(d, guildId, targetId);
  return {
    isMe,
    targetIsOwner,
    iAmOwner,
    outranks,
    blockedByHierarchy: !isMe && !outranks && holdsRanked,
    voiceChannelId,
    timedOutUntil,
    serverMuted,
    serverDeafened,
    channelMuted,
    channelDeafened,
    nick: isMe
      ? can(base, Permissions.CHANGE_NICKNAME) || can(base, Permissions.MANAGE_NICKNAMES)
      : mod(can(base, Permissions.MANAGE_NICKNAMES)),
    poke: poke.shown && !poke.blocked,
    pokeBlocked: poke.blocked,
    timeout: mod(can(base, Permissions.MODERATE_MEMBERS) && !targetAdmin),
    kick: mod(can(base, Permissions.KICK_MEMBERS)),
    ban: mod(can(base, Permissions.BAN_MEMBERS)),
    // voice: no hierarchy, and on yourself too (Discord; the server's
    // rule). Out of voice `vperm` is the server level, the server's rule there.
    mute: (inVoice || serverMuted) && can(vperm, Permissions.MUTE_MEMBERS),
    deafen: (inVoice || serverDeafened) && can(vperm, Permissions.DEAFEN_MEMBERS),
    // the channel mute and deafen: the same rule, in the channel they are
    // in, by that channel's permissions (so the owner, or a moderator,
    // channel-muted there can lift it from their own menus)
    channelMute: !!vc && can(vperm, Permissions.MUTE_MEMBERS),
    channelDeafen: !!vc && can(vperm, Permissions.DEAFEN_MEMBERS),
    // yourself (another device or tab): switching or leaving, no Move
    // Members (Discord; the server's `checkSelfMove`)
    move: inVoice && (isMe || can(vperm, Permissions.MOVE_MEMBERS)),
    disconnect: inVoice && (isMe || can(vperm, Permissions.MOVE_MEMBERS)),
    transfer: iAmOwner && !isMe && !!member && !member.user.bot,
    roles: !!member && assignableRoles(d, guildId).length > 0,
  };
}

/**
 * Voice channels the target can be moved to: you need Move Members and
 * Connect there (Discord; the server checks the same, without your channel
 * bans, which the client does not have). Their own Connect does not count:
 * Discord's Move Members takes people into rooms they could not join (a
 * staff room), and the server keeps them there until they leave
 * (`VoiceService.allowMovedIn`). Before, those rooms were missing here and
 * comms-voice refused them, which the server reported as "not connected to
 * voice". Yourself: a plain switch, every voice channel you may connect to
 * (the server's join rules decide the rest).
 */
export function moveTargets(d: DataState, guildId: Snowflake, targetId: Snowflake): Snowflake[] {
  const self = targetId === d.me?.id;
  const need = self ? Permissions.CONNECT : Permissions.MOVE_MEMBERS | Permissions.CONNECT;
  // in the sidebar's order (a position counts inside its own category)
  return channelsInSidebarOrder(d, guildId)
    .filter(
      (c) =>
        (c.type === ChannelType.GUILD_VOICE || c.type === ChannelType.GUILD_STAGE_VOICE) &&
        can(channelPermissions(d, c), need),
    )
    .map((c) => c.id);
}

/** Server-level tools the Server settings dialog offers. */
export interface ServerTools {
  /** Manage Server: the name and icon (Server settings › Overview). */
  overview: boolean;
  channels: boolean;
  roles: boolean;
  invites: boolean;
  /** Manage Server: see every invite, not only your own. */
  allInvites: boolean;
  audit: boolean;
  bans: boolean;
  deleted: boolean;
  owner: boolean;
}

export function serverTools(d: DataState, guildId: Snowflake | null): ServerTools {
  if (!guildId)
    return {
      overview: false,
      channels: false,
      roles: false,
      invites: false,
      allInvites: false,
      audit: false,
      bans: false,
      deleted: false,
      owner: false,
    };
  const base = basePermissions(d, guildId);
  // the owner's tools are not part of a "View server as role" preview
  const owner = d.guilds[guildId]?.owner_id === d.me?.id && !viewAsRoles(d, guildId);
  const admin = can(base, Permissions.ADMINISTRATOR);
  return {
    overview: can(base, Permissions.MANAGE_GUILD),
    channels: can(base, Permissions.MANAGE_CHANNELS),
    roles: can(base, Permissions.MANAGE_ROLES),
    invites: can(base, Permissions.CREATE_INSTANT_INVITE) || can(base, Permissions.MANAGE_GUILD),
    allInvites: can(base, Permissions.MANAGE_GUILD),
    audit: can(base, Permissions.VIEW_AUDIT_LOG),
    bans: can(base, Permissions.BAN_MEMBERS),
    // "The owner and Admins restore it" (§4.5)
    deleted: owner || admin,
    owner,
  };
}
